<template>
  <div class="template-categories-container">
    <div class="template-categories-main">
      <!-- 左侧分类树 -->
      <div class="category-tree-wrapper">
        <div class="category-tree-header">
          <p class="add-category-btn" @click="addCategoryAction()">
            <i class="el-icon-plus"></i> 添加二级分类
          </p>
        </div>
        <el-scrollbar class="category-tree-scrollbar">
          <el-tree ref="treeRef" :data="treeData" node-key="id" :props="{ children: 'children', label: 'name' }"
            draggable :allow-drop="allowDrop" @node-drop="handleDrop" @node-click="handleNodeClick"
            :highlight-current="true">
            <div slot-scope="{ node, data }" class="tree-node-content">
              <div class="tree-node-label">
                {{ getCategoryName(data) }}
              </div>
              <div class="tree-node-actions">
                <i class="el-icon-remove tree-action-icon tree-action-delete" @click.stop="delCategoryAction(data)"></i>
                <!-- <i v-if="data.hierarchyLevel === 1" class="el-icon-circle-plus tree-action-icon tree-action-add"
                  @click.stop="addCategoryAction(data)"></i> -->
                <i class="el-icon-edit tree-action-icon tree-action-edit" @click.stop="updateCategoryAction(data)"></i>
              </div>
            </div>
          </el-tree>
        </el-scrollbar>
      </div>

      <!-- 右侧模版列表 -->
      <div v-if="currentCategory" class="data-list-wrapper">
        <div class="data-list-header">
          <p class="add-data-btn" @click="addDataVisible = !addDataVisible">
            <i class="el-icon-plus"></i> 添加模版素材
          </p>
        </div>
        <div id="sortable-container" v-infinite-scroll="dataListLoad" :infinite-scroll-immediate="false"
          :infinite-scroll-disabled="noMoreData" class="sortable-container">
          <div v-for="(data, index) in categoryDataList" :key="index" class="data-card">
            <div class="data-card-left">
              <!-- 缩略图 -->
              <div class="thumbnail-wrapper">
                <el-image v-if="getThumbnail(data)" :src="getThumbnail(data)" fit="fill" class="thumbnail-image"
                  :preview-src-list="[getThumbnail(data)]" :lazy="true"></el-image>
                <p v-if="getThumbnailBadge(data)" class="thumbnail-badge">
                  {{ getThumbnailBadge(data) }}
                </p>
              </div>
              <!-- 模版信息 -->
              <div class="data-info">
                <p class="data-title">
                  {{ getTitle(data) }}
                </p>
                <div class="data-tags">
                  <div v-for="tag in (data.tags || [])" :key="tag.id || tag" class="tag-item" :title="getTagName(tag)">
                    {{ getTagName(tag) }}
                  </div>
                </div>
                <p class="data-slogan">
                  {{ getSlogan(data) }}
                </p>
                <p class="data-publisher">
                  {{ (data.publisher && data.publisher.name) ? data.publisher.name : "-" }}
                </p>
              </div>
            </div>

            <div class="data-card-right">
              <p class="data-price">¥ {{ getMinPrice(data.paymentInfo) }}</p>
              <p class="data-usage-count">{{ data.totalUsageCount || 0 }}人正在使用</p>
              <div class="payment-button-wrapper">
                <div class="payment-button">
                  <p class="payment-button-text">
                    {{ getPaymentTypeText(data.paymentInfo && data.paymentInfo.type) }}
                  </p>
                </div>
                <div v-if="getButtonBadge(data)" class="button-badge-wrapper">
                  <div class="button-badge" :style="{ background: data.buttonBadgeColor || '#409EFF' }">
                    <p class="badge-text">
                      {{ getButtonBadge(data) }}
                    </p>
                  </div>
                  <div class="badge-tail" :style="{
                    background: 'radial-gradient(50% 50%, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 100%, ' + (data.buttonBadgeColor || '#409EFF') + ' 0%)',
                    right: 0
                  }">
                    <div class="badge-tail-inner"></div>
                  </div>
                </div>
              </div>
            </div>

            <div class="data-card-actions">
              <i class="el-icon-delete action-icon action-delete" @click="deleteData(data)"></i>
              <i class="el-icon-rank drag-btn action-icon action-drag" @mouseenter="initDataListSortable"></i>
            </div>
          </div>

          <p v-if="loading" class="loading-text">正在加载~</p>
          <p v-if="noMoreData" class="no-more-text">数据已经全部加载完啦~</p>
        </div>
      </div>

      <div v-else class="empty-state">
        <p>请选择分类</p>
      </div>
    </div>

    <!-- 添加/编辑分类对话框：复用现有组件 -->
    <add-category-dialog :categoryVisible.sync="categoryVisible" :formData.sync="formData"
      :operationTitle="operationTitle" :categoryDialogTitle="categoryDialogTitle"
      @handleCategoryDialogClose="handleCategoryDialogClose"
      @handleCategoryDialogConfirm="handleCategoryDialogConfirm" />

    <!-- 添加模版对话框 -->
    <el-dialog title="添加模版素材" :visible.sync="addDataVisible" fullscreen :close-on-click-modal="false"
      :close-on-press-escape="false" destroy-on-close class="add-data-dialog-wrapper">
      <add-data-dialog :currentCategoryId="currentCategory ? currentCategory.id : null" @close="addDataVisible = false"
        @success="handleTemplateCategoryData" />
    </el-dialog>
  </div>
</template>

<script>
import Sortable from "sortablejs";
import api from "@/api";
import { valueByLanguage } from "@/utils/college";
import { calculateDynamicOrdinal, resetDataOrdinal } from "@/utils/ordinalUtils";
import AddCategoryDialog from "./components/addCategoryDialog/index.vue";
import AddDataDialog from "./components/addDataDialog/index.vue";

const BtnTextMap = {
  FREE: "下载",
  PREMIUM: "成为Pro会员",
  VIP_PRO: "Pro会员领取"
};

export default {
  name: "TemplateCategories",
  components: {
    AddCategoryDialog,
    AddDataDialog
  },
  data() {
    return {
      treeData: [],
      currentCategory: null,
      categoryDetails: null,
      categoryVisible: false,
      operationTitle: "",
      addDataVisible: false,

      formData: {
        nameZh: "",
        nameTw: "",
        nameEn: "",
        parentId: null
      },

      dataListParams: {
        page: 1,
        pageSize: 20
      },
      categoryDataList: [],
      categoryDataTotal: 0,
      loading: false,

      sortableInstance: null
    };
  },
  computed: {
    categoryDialogTitle() {
      return `${!this.categoryDetails ? "添加" : "编辑"}${this.operationTitle}`;
    },
    noMoreData() {
      return (
        this.categoryDataList.length &&
        this.categoryDataList.length >= this.categoryDataTotal
      );
    }
  },
  mounted() {
    this.getCategoryTree();
  },
  beforeDestroy() {
    if (this.sortableInstance) {
      this.sortableInstance.destroy();
    }
  },
  methods: {
    // ===== tree =====
    getCategoryTree() {
      api
        .getTemplateCategoryTree({ page: 1, pageSize: 1000 })
        .then((res) => {
          this.treeData = (res.data && res.data.items) ? res.data.items : [];
        })
        .catch((err) => {
          this.$message.error((err && err.message) || "获取分类树失败");
        });
    },
    getCategoryName(data) {
      const langData = valueByLanguage(data && data.name, "zh_CN");
      return (langData && langData.value) || "";
    },
    handleNodeClick(data, node, component) {
      if (!data) return;
      this.currentCategory = data;
      this.categoryDataList = [];
      this.dataListParams.page = 1;
      this.getTemplateListByCategory();
    },
    allowDrop(draggingNode, dropNode, type) {
      if (draggingNode.level === dropNode.level) {
        if (draggingNode.data.parentId === dropNode.data.parentId) {
          return type === "prev" || type === "next";
        }
        return false;
      }
      return false;
    },
    async handleDrop(dragNode) {
      try {
        const tempCurrentNode = this.$refs.treeRef.getNode(dragNode.data.id);
        const currentNode = tempCurrentNode.data;
        const nextNode = tempCurrentNode.nextSibling ? tempCurrentNode.nextSibling.data : null;
        const prevNode = tempCurrentNode.previousSibling ? tempCurrentNode.previousSibling.data : null;

        if (!prevNode && nextNode) {
          currentNode.ordinal = nextNode.ordinal - 0.01;
        } else if (!nextNode && prevNode) {
          currentNode.ordinal = Date.now();
        } else if (prevNode && nextNode) {
          currentNode.ordinal = (prevNode.ordinal + nextNode.ordinal) / 2;
        }

        currentNode.ordinal = Math.round(currentNode.ordinal * 100) / 100;
        await api.updateTemplateCategoryOrdinal(currentNode.id, { ordinal: currentNode.ordinal });
      } catch (err) {
        // ignore
      }
    },

    // ===== category actions =====
    addCategoryAction(data = null) {
      this.formData.parentId = data ? data.id : null;
      this.categoryDetails = null;
      this.operationTitle = !data
        ? "二级分类"
        : data.hierarchyLevel === 1
          ? "三级分类"
          : "四级分类";

      this.formData = {
        nameZh: "",
        nameTw: "",
        nameEn: "",
        parentId: this.formData.parentId
      };
      this.categoryVisible = true;
    },
    async updateCategoryAction(data) {
      try {
        const res = await api.getTemplateCategoryDetails(data.id);
        this.categoryDetails = res.data;
        const name = (this.categoryDetails && this.categoryDetails.name) ? this.categoryDetails.name : [];
        this.formData = {
          nameZh: valueByLanguage(name, "zh_CN") || "",
          nameTw: valueByLanguage(name, "zh_TW") || "",
          nameEn: valueByLanguage(name, "en") || "",
          parentId: this.categoryDetails.parentId
        };
        this.operationTitle =
          data.hierarchyLevel === 1
            ? "二级分类"
            : data.hierarchyLevel === 2
              ? "三级分类"
              : "四级分类";
        this.categoryVisible = true;
      } catch (e) { }
    },
    delCategoryAction(data) {
      // 检查是否有子分类：通过 treeRef 获取节点信息，检查是否有子节点
      const treeNode = this.$refs.treeRef.getNode(data.id);
      const hasChildren = (treeNode && treeNode.childNodes && treeNode.childNodes.length > 0) ||
        (data.children && data.children.length > 0);
      
      if (hasChildren) {
        this.$message.warning("该分类下存在子分类，请先删除所有子分类后再删除该分类");
        return;
      }

      this.$confirm("删除后所有内容与分类均不在展示，是否确认删除?", "提示", {
        confirmButtonText: "确认",
        cancelButtonText: "取消",
        type: "warning",
        center: true
      })
        .then(async () => {
          try {
            await api.delTemplateCategory(data.id);
            // ElementUI tree remove by node-key requires node instance; simplest: reload tree
            this.getCategoryTree();
            if (this.currentCategory && this.currentCategory.id === data.id) {
              this.currentCategory = null;
              this.categoryDataList = [];
            }
            this.$message.success("删除成功");
          } catch (err) {
            const errorMessage = (err && err.message) || "删除失败";
            // 如果是外键约束错误，给出更友好的提示
            if (errorMessage.includes("foreign key constraint") || errorMessage.includes("外键约束")) {
              this.$message.error("该分类下存在子分类，无法删除。请先删除所有子分类后再删除该分类");
            } else {
              this.$message.error(errorMessage);
            }
          }
        })
        .catch(() => { });
    },
    handleCategoryDialogClose() {
      this.categoryDetails = null;
      this.formData = {
        nameZh: "",
        nameTw: "",
        nameEn: "",
        parentId: null
      };
    },
    async handleCategoryDialogConfirm(formDataWithName) {
      try {
        // 使用子组件传递过来的数据，确保格式正确
        const payload = {
          name: formDataWithName.name,
          parentId: formDataWithName.parentId
        };

        if (this.categoryDetails) {
          await api.updateTemplateCategory(this.categoryDetails.id, payload);
        } else {
          await api.addTemplateCategory(payload);
        }

        this.categoryVisible = false;
        this.handleCategoryDialogClose();
        this.getCategoryTree();
      } catch (e) { }
    },

    // ===== right list =====
    getTemplateListByCategory() {
      if (!this.currentCategory) return;
      this.loading = true;
      api
        .getTemplateList({
          ...this.dataListParams,
          categoryIds: this.currentCategory.id
        })
        .then((res) => {
          const items = (res.data && res.data.items) ? res.data.items : [];
          this.categoryDataList = [...this.categoryDataList, ...items];
          this.categoryDataTotal = (res.data && res.data.count) ? res.data.count : 0;
          this.loading = false;
        })
        .catch((err) => {
          this.$message.error((err && err.message) || "获取模版列表失败");
          this.loading = false;
        });
    },
    dataListLoad() {
      if (this.noMoreData) return;
      this.loading = true;
      this.dataListParams.page += 1;
      this.getTemplateListByCategory();
    },
    initDataListSortable(e) {
      if (e && e.preventDefault) e.preventDefault();
      const el = document.querySelector("#sortable-container");
      if (!el) return;
      if (this.sortableInstance) return;

      this.sortableInstance = Sortable.create(el, {
        animation: 150,
        handle: ".drag-btn",
        onEnd: async (evt) => {
          const oldIndex = evt.oldIndex;
          const newIndex = evt.newIndex;
          if (oldIndex === newIndex) return;
          try {
            const currentNode = this.categoryDataList.splice(oldIndex, 1)[0];
            this.categoryDataList.splice(newIndex, 0, currentNode);

            const newArray = [...this.categoryDataList];
            this.categoryDataList = [];
            await this.$nextTick();
            this.categoryDataList = newArray;

            const newOrdinal = calculateDynamicOrdinal(newIndex, this.categoryDataList);
            if (newOrdinal === null) {
              const list = resetDataOrdinal({ dataList: this.categoryDataList });
              await Promise.all(
                list.map((item) =>
                  api.updateTemplateOrdinalInCategory(this.currentCategory.id, item.id, { ordinal: item.ordinal })
                )
              );
              this.categoryDataList = list;
              return;
            }

            currentNode.ordinal = newOrdinal;
            await api.updateTemplateOrdinalInCategory(this.currentCategory.id, currentNode.id, { ordinal: newOrdinal });
          } catch (err) { }
        }
      });
    },
    deleteData(data) {
      this.$confirm("移除内容后，则不在此分类下展示，是否确认移除?", "提示", {
        confirmButtonText: "确认",
        cancelButtonText: "取消",
        type: "warning",
        center: true
      })
        .then(async () => {
          await api.delDataFromTemplateCategory(this.currentCategory.id, { itemIds: [data.id] });
          this.categoryDataList = this.categoryDataList.filter((item) => item.id !== data.id);
        })
        .catch(() => { });
    },
    async handleTemplateCategoryData(payload) {
      const selectedData = payload && payload.selectedData ? payload.selectedData : [];
      const delSelectedData = payload && payload.delSelectedData ? payload.delSelectedData : [];
      await Promise.all([
        selectedData.length
          ? api.addDataToTemplateCategory(this.currentCategory.id, { itemIds: selectedData.map((i) => i.id) })
          : Promise.resolve(),
        delSelectedData.length
          ? api.delDataFromTemplateCategory(this.currentCategory.id, { itemIds: delSelectedData.map((i) => i.id) })
          : Promise.resolve()
      ]);
      this.$message.success("操作成功");
      this.addDataVisible = false;
      this.handleNodeClick(this.currentCategory);
    },

    // ===== helpers =====
    getThumbnail(row) {
      return valueByLanguage(row && row.thumbnail, "zh_CN", true) || "";
    },
    getThumbnailBadge(row) {
      return valueByLanguage(row && row.thumbnailBadge, "zh_CN", true) || "";
    },
    getTitle(row) {
      return valueByLanguage(row && row.title, "zh_CN", true) || "";
    },
    getSlogan(row) {
      return valueByLanguage(row && row.slogan, "zh_CN", true) || "";
    },
    getButtonBadge(row) {
      return valueByLanguage(row && row.buttonBadge, "zh_CN", true) || "";
    },
    getTagName(tag) {
      if (!tag) return "-";
      const rawName = (() => {
        if (tag.name) {
          if (Array.isArray(tag.name)) {
            return valueByLanguage(tag.name, "zh_CN", true) || "";
          }
          return String(tag.name || "");
        }
        if (typeof tag === "string") {
          return tag;
        }
        return "";
      })();
      return rawName || "-";
    },
    getPaymentTypeText(type) {
      return BtnTextMap[type] || "-";
    },
    getMinPrice(paymentInfo) {
      if (!paymentInfo) return 0;
      const keys = Object.keys(paymentInfo).filter((k) => k.indexOf("price") === 0 && paymentInfo[k] !== null);
      const prices = keys.map((k) => paymentInfo[k]).filter((v) => typeof v === "number");
      if (!prices.length) return 0;
      return Math.min.apply(null, prices) / 100;
    }
  }
};
</script>

<style scoped>
.template-categories-container {
  height: calc(100vh - 135px);
}

.template-categories-main {
  display: flex;
  width: 100%;
  height: 100%;
  background: #ffffff;
}

.category-tree-wrapper {
  display: flex;
  flex-direction: column;
  width: 250px;
  min-width: 250px;
  border-right: 1px solid #f5f5f5;
  flex-shrink: 0;
}

.category-tree-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  border-bottom: 1px solid #f5f5f5;
}

.add-category-btn {
  display: flex;
  align-items: center;
  font-size: 14px;
  padding: 8px 16px;
  color: #409eff;
  cursor: pointer;
}

.category-tree-scrollbar {
  flex: 1;
}

.tree-node-content {
  overflow: hidden;
  flex: 1;
  height: 48px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 8px;
  font-size: 14px;
  font-weight: 500;
}

.tree-node-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tree-node-actions {
  display: none;
  align-items: center;
  gap: 8px;
}

.tree-action-icon {
  font-size: 18px;
  cursor: pointer;
}

.tree-action-delete {
  color: #f56c6c;
}

.tree-action-add {
  color: #67c23a;
}

.tree-action-edit {
  color: #409eff;
}

:deep(.el-tree-node__content) {
  height: 100%;
  margin-top: 10px;
}

:deep(.el-tree-node__content:hover) {
  background-color: rgba(64, 158, 255, 0.08);
}

:deep(.el-tree-node__content:hover) .tree-node-actions {
  display: flex;
}

:deep(.el-tree-node.is-current > .el-tree-node__content) {
  background-color: rgba(64, 158, 255, 0.08);
}

.data-list-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.data-list-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  border-bottom: 1px solid #f5f5f5;
}

.add-data-btn {
  display: flex;
  align-items: center;
  font-size: 14px;
  padding: 8px 16px;
  color: #409eff;
  cursor: pointer;
}

.sortable-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  overflow: auto;
}

.data-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 128px;
  background: #f9fafb;
  padding: 16px;
  border-radius: 8px;
}

.data-card-left {
  display: flex;
  gap: 16px;
  align-items: center;
  flex: 1;
  overflow: hidden;
  min-width: 400px;
}

.thumbnail-wrapper {
  width: 80px;
  height: 114px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  text-align: center;
  flex-shrink: 0;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
}

.thumbnail-badge {
  position: absolute;
  right: 0;
  top: 0;
  border-bottom-left-radius: 12px;
  background: #f1f1f1;
  font-size: 12px;
  padding: 0 12px;
}

.data-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  overflow: hidden;
}

.data-title {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  overflow: hidden;
}

.tag-item {
  color: #67c23a;
  font-size: 12px;
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
  border-radius: 4px;
  padding: 0 6px;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-slogan {
  font-size: 14px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.data-publisher {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-card-right {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.data-price,
.data-usage-count {
  font-size: 14px;
}

.payment-button-wrapper {
  position: relative;
  cursor: pointer;
}

.payment-button {
  background: rgba(98, 90, 255, 0.05);
  border-radius: 4px;
  padding: 4px 24px;
  min-width: 76px;
  text-align: center;
}

.payment-button-text {
  font-size: 12px;
  font-weight: 500;
  color: #409eff;
}

.button-badge-wrapper {
  position: absolute;
  right: 0;
  top: -14px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.button-badge {
  height: 16px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
  border-top-right-radius: 12px;
}

.badge-text {
  min-width: 50px;
  max-width: 80px;
  text-align: center;
  font-size: 10px;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.badge-tail {
  position: relative;
  width: 6px;
  height: 6px;
  overflow: hidden;
}

.badge-tail-inner {
  position: absolute;
  top: 0;
  width: 12px;
  height: 12px;
  right: 0;
}

.data-card-actions {
  display: flex;
  align-items: center;
  gap: 20px;
}

.action-icon {
  font-size: 20px;
  cursor: pointer;
}

.action-delete {
  color: #f56c6c;
}

.action-drag {
  color: #909399;
  cursor: grab;
}

.loading-text,
.no-more-text {
  font-size: 14px;
  text-align: center;
  color: #409eff;
}

.empty-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
}

/* 添加模版素材对话框样式 */
:deep(.add-data-dialog-wrapper .el-dialog__header) {
  padding: 20px 20px 15px;
  border-bottom: 1px solid #e4e7ed;
}

:deep(.add-data-dialog-wrapper .el-dialog__header .el-dialog__close) {
  /* display: none !important; */
}

:deep(.add-data-dialog-wrapper .el-dialog__body) {
  padding: 16px;
  height: calc(100vh - 100px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>
